// An intent's progress as the user sees it, from the api's row and timeline (docs/builders-ui.md
// §4). Optimistic fill: a solver fills straight from `opened`, so there is no attested step (the
// open is never attested at all). The user is done at `fulfilled` (paid) or `refunded`;
// `settled` is the solver being repaid. Received on Jupnet (`gum_deposit`), the fill is a deposit
// into gum's bank and the user is done only once Jupnet credits it (`credit_tx`): the credit and
// the settle are independent, so a settled intent can still be waiting on it.

import type { IntentEvent, IntentRow } from "./api";

export type StepKey = "opened" | "fulfilled" | "credited" | "settled" | "expired" | "voided" | "refunded";
export type Step = {
  key: StepKey;
  label: string;
  status: "done" | "active" | "pending";
  at?: number;
  /** ms since the previous step that happened */
  delta?: number;
  tx?: string | null;
  /** which side the tx is on */
  side?: "src" | "dst" | "jupnet";
};
export type Progress = {
  lane: "fill" | "refund";
  steps: Step[];
  /** paid (credited on Jupnet, for a gum_deposit) or refunded: nothing more for the user to wait for */
  userDone: boolean;
  /** nothing more will happen: refunded, or settled (and credited, for a gum_deposit) */
  final: boolean;
  headline: string;
  totalMs?: number;
};

type Plan = { key: StepKey; label: string; side: Step["side"] }[];
const FILL: Plan = [
  { key: "opened", label: "Locked on the source chain", side: "src" },
  { key: "fulfilled", label: "Delivered to the recipient", side: "dst" },
  { key: "settled", label: "Solver repaid", side: "src" },
];
const DEPOSIT: Plan = [
  { key: "opened", label: "Locked on the source chain", side: "src" },
  { key: "fulfilled", label: "Deposited into gum's bank", side: "dst" },
  { key: "credited", label: "Credited on Jupnet", side: "jupnet" },
  { key: "settled", label: "Solver repaid", side: "src" },
];
const REFUND: Plan = [
  { key: "opened", label: "Locked on the source chain", side: "src" },
  { key: "expired", label: "Expired unfilled", side: "dst" },
  { key: "voided", label: "Voided on the destination", side: "dst" },
  { key: "refunded", label: "Refunded to you", side: "src" },
];

const txOf = (row: IntentRow, k: StepKey): string | null =>
  ({ opened: row.open_tx, fulfilled: row.fulfill_tx, credited: row.credit_tx, settled: row.settle_tx, expired: null, voided: row.void_tx, refunded: row.refund_tx })[k] ?? null;

/** The timeline row of a Jupnet credit: its `state` is the intent's at the time, not a step. */
const CREDIT_EVIDENCE = "DepositCompletedV2";
export const isGumDeposit = (row: IntentRow) => row.delivery === 1;

/** Timeline states that count as reaching a step (`void_attested` is still the voided step). */
const reaches: Record<StepKey, string[]> = {
  opened: ["opened"],
  fulfilled: ["fulfilled", "fulfill_attested"],
  credited: [],
  settled: ["settled"],
  expired: ["expired"],
  voided: ["voided", "void_attested"],
  refunded: ["refunded"],
};

export function progress(row: IntentRow, events: IntentEvent[]): Progress {
  const credit = events.find((e) => e.evidence === CREDIT_EVIDENCE);
  const timeline = events.filter((e) => e.evidence !== CREDIT_EVIDENCE);
  const first = (states: string[]) => {
    const e = timeline.find((e) => states.includes(e.state));
    return e ? Date.parse(e.at) : undefined;
  };
  const filled = row.fulfill_tx != null || timeline.some((e) => reaches.fulfilled.includes(e.state)) || ["fulfilled", "fulfill_attested", "settled"].includes(row.state);
  const refundLane = !filled && ["expired", "voided", "void_attested", "refunded"].some((s) => row.state === s || timeline.some((e) => e.state === s));
  const lane = refundLane ? "refund" : "fill";
  const deposit = isGumDeposit(row);
  const credited = row.credit_tx != null || credit !== undefined;
  const plan = lane === "refund" ? REFUND : deposit ? DEPOSIT : FILL;

  const byRow: Record<StepKey, boolean> = {
    opened: true,
    fulfilled: filled,
    credited,
    settled: row.state === "settled",
    expired: ["expired", "voided", "void_attested", "refunded"].includes(row.state),
    voided: ["voided", "void_attested", "refunded"].includes(row.state),
    refunded: row.state === "refunded",
  };
  const ats = plan.map((p) => (p.key === "credited" ? (credit ? Date.parse(credit.at) : undefined) : (first(reaches[p.key]) ?? (p.key === "opened" ? Date.parse(row.created_at) : undefined))));
  // a step is done when it, or any later step, was reached (the row can be ahead of the timeline);
  // the credit is the exception: it does not follow from the settle, so only its own record counts
  let furthest = -1;
  plan.forEach((p, i) => {
    if (p.key !== "credited" && (ats[i] !== undefined || byRow[p.key])) furthest = i;
  });
  const final = (row.state === "settled" && (!deposit || credited)) || row.state === "refunded";
  const steps: Step[] = plan.map((p, i) => {
    const at = ats[i];
    const status: Step["status"] =
      p.key === "credited" ? (credited ? "done" : filled ? "active" : "pending") : i <= furthest ? "done" : i === furthest + 1 && !final ? "active" : "pending";
    // since the latest earlier step that happened before it
    const before = ats.slice(0, i).filter((t): t is number => t !== undefined && at !== undefined && t <= at);
    const delta = at !== undefined && before.length ? at - Math.max(...before) : undefined;
    return { key: p.key, label: p.label, status, at, delta, tx: p.key === "credited" ? (row.credit_tx ?? credit?.tx ?? null) : txOf(row, p.key), side: p.side };
  });

  const userDone = lane === "fill" ? filled && (!deposit || credited) : row.state === "refunded";
  const startedAt = steps[0]?.at;
  const doneAt = steps.find((s) => s.key === (lane === "refund" ? "refunded" : deposit ? "credited" : "fulfilled"))?.at;
  return {
    lane,
    steps,
    userDone,
    final,
    headline: headline(row, lane, userDone, filled),
    totalMs: userDone && startedAt !== undefined && doneAt !== undefined ? doneAt - startedAt : undefined,
  };
}

function headline(row: IntentRow, lane: "fill" | "refund", userDone: boolean, filled: boolean): string {
  if (lane === "fill") return userDone ? "Done ✓" : filled ? "Deposited — waiting for Jupnet to credit it…" : "Waiting for a solver to fill…";
  if (row.state === "refunded") return "Refunded";
  if (row.state === "expired") return "No solver filled in time — refund starting";
  return "Refund in progress…";
}

export function fmtMs(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  const m = Math.floor(ms / 60_000);
  return `${m} min ${Math.round((ms % 60_000) / 1000)} s`;
}
